import * as THREE from 'three';
import type { BlendshapeFrame } from './chat/types';
import { CHANNELS } from '../../../packages/protocol/generated/ts/channels';

/**
 * Blendshape Animation Manager
 *
 * Handles facial animation using morph targets (blendshapes) from the server.
 * Frame format and channel order come from packages/protocol: a timecode followed by 68 channel
 * values, at the frame rate the gateway reports with each batch.
 */
export class BlendshapeAnimator {
  private meshes: THREE.Mesh[] = [];
  private currentFrameIndex = 0;
  private frames: BlendshapeFrame[] = [];
  private isPlaying = false;
  private startTime = 0;
  private animationFrameId: number | null = null;
  private knipperAction: THREE.AnimationAction | null = null;

  // Only 'api' channels carry data. The idle animation owns blink, gaze and head, and the emotion
  // channels are 'unused'; the gateway sends 0 for both, which would overwrite or do nothing.
  private readonly API_CHANNELS = CHANNELS.filter((channel) => channel.owner === 'api');
  private frameDurationMs = 1000 / 30;

  constructor(scene: THREE.Scene) {
    this.collectMeshes(scene);
    this.reportMissingChannels();
  }

  // applyFrame skips a channel no mesh has a morph target for; say so once instead of on every frame.
  private reportMissingChannels() {
    const missing = this.API_CHANNELS
      .filter((channel) => !this.meshes.some((mesh) => mesh.morphTargetDictionary?.[channel.name] !== undefined))
      .map((channel) => channel.name);
    if (missing.length > 0) {
      console.error('[BlendshapeAnimator] model has no morph target for these channels; they will not move:', missing);
    }
  }

  /**
   * Recursively traverse the scene and collect all meshes with morph targets
   */
  private collectMeshes(object: THREE.Object3D) {

    // Check if current object is a mesh with morph targets
    if (object instanceof THREE.Mesh) {
      if (object.morphTargetInfluences && object.morphTargetInfluences.length > 0) {
        this.meshes.push(object);
      }
    }

    // Recursively check all children (this handles Groups, Objects, and other container types)
    if (object.children && object.children.length > 0) {
      object.children.forEach((child: THREE.Object3D) => {
        this.collectMeshes(child);
      });
    }
  }

  /**
   * Recollect meshes from the scene
   * Call this method after loading a new model to reinitialize the mesh collection
   */
  public recollectMeshes(object: THREE.Object3D) {
    this.meshes = [];
    this.collectMeshes(object);
    this.reportMissingChannels();
  }

  /**
   * Set animation frames from server response
   * Each frame: [timestamp, value0, value1, ..., value68] (70 elements total)
   */
  setFrames(frames: readonly BlendshapeFrame[], frameRate: number) {
    this.frames = [...frames];
    this.frameDurationMs = 1000 / frameRate;
    this.currentFrameIndex = 0;
  }

  setKnipperAction(action: THREE.AnimationAction | null) {
    this.knipperAction = action;
  }

  play() {
    if (this.isPlaying) return;
    if (this.frames.length === 0) {
      console.warn('[BlendshapeAnimator] No frames to play');
      return;
    }

    if (this.knipperAction) this.knipperAction.paused = true;

    this.isPlaying = true;
    this.currentFrameIndex = 0;
    this.startTime = performance.now();
    this.applyFrame(this.frames[0]);
    this.animate();
  }

  // Closes the mouth: the last frame of a chunk can still be mid-word, and it would otherwise
  // stay on the face after the answer and during a wait for the next chunk.
  stop() {
    this.isPlaying = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.resetApiChannels();
    if (this.knipperAction) this.knipperAction.paused = false;
  }

  /**
   * Reset animation state and all blendshapes to neutral
   */
  reset() {
    this.stop();
    this.currentFrameIndex = 0;
    this.resetAllBlendshapes();
  }

  /**
   * Reset all blendshape influences to 0 (neutral position)
   */
  private resetAllBlendshapes() {
    this.meshes.forEach((mesh) => {
      if (mesh.morphTargetInfluences) {
        mesh.morphTargetInfluences.fill(0);
      }
    });
  }

  private resetApiChannels() {
    this.meshes.forEach((mesh) => {
      for (const channel of this.API_CHANNELS) {
        const morphIndex = mesh.morphTargetDictionary?.[channel.name];
        if (morphIndex !== undefined && mesh.morphTargetInfluences) {
          mesh.morphTargetInfluences[morphIndex] = 0;
        }
      }
    });
  }

  private applyFrame(frame: BlendshapeFrame) {
    if (!frame || frame.length !== CHANNELS.length + 1) {
      console.warn('[BlendshapeAnimator] Invalid frame, expected a timecode and', CHANNELS.length, 'values');
      return;
    }

    this.meshes.forEach((mesh) => {
      for (const channel of this.API_CHANNELS) {
        const morphIndex = mesh.morphTargetDictionary?.[channel.name];
        if (morphIndex !== undefined && mesh.morphTargetInfluences) {
          // Positions count from 1 and position 1 is the timecode, so a channel sits at index position - 1.
          mesh.morphTargetInfluences[morphIndex] = frame[channel.position - 1] as number;
        }
      }
    });
  }

  private animate = () => {
    const elapsed = performance.now() - this.startTime;
    const currentFrameIndex = Math.floor(elapsed / this.frameDurationMs);

    if (currentFrameIndex >= this.frames.length) {
      this.stop();
      return;
    }

    if (currentFrameIndex !== this.currentFrameIndex) {
      this.currentFrameIndex = currentFrameIndex;
      const frame = this.frames[this.currentFrameIndex];
      if (frame) this.applyFrame(frame);
    }

    this.animationFrameId = requestAnimationFrame(this.animate);
  };

  dispose() {
    this.stop();
    this.meshes = [];
    this.frames = [];
  }
}

